import { useMutation, useQueryClient } from '@tanstack/react-query';
import { emailDomainsApi } from '../services';
import type { CreateEmailDomainRequest, UpdateEmailDomainRequest } from '@troia-v3/types';

/**
 * Hook com todas as mutations para dominios de email.
 *
 * Invalida automaticamente a query ['email-domains'] apos cada operacao.
 *
 * @example
 * ```tsx
 * const { createDomain, updateDomain, deleteDomain, verifyDomain } = useEmailDomainMutations();
 *
 * createDomain.mutate({ domain: 'example.com' });
 * ```
 */
export const useEmailDomainMutations = () => {
  const queryClient = useQueryClient();

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: ['email-domains'] });
  };

  /** Cria um novo dominio de email */
  const createDomain = useMutation({
    mutationFn: (data: CreateEmailDomainRequest) => emailDomainsApi.create(data),
    onSuccess: invalidate,
  });

  /** Atualiza configuracoes de um dominio */
  const updateDomain = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEmailDomainRequest }) =>
      emailDomainsApi.update(id, data),
    onSuccess: invalidate,
  });

  /** Remove um dominio */
  const deleteDomain = useMutation({
    mutationFn: (id: string) => emailDomainsApi.delete(id),
    onSuccess: invalidate,
  });

  /** Verifica DNS: busca status atualizado do Resend e dispara re-verificacao se necessario */
  const verifyDomain = useMutation({
    mutationFn: (id: string) => emailDomainsApi.verify(id),
    onSuccess: invalidate,
  });

  return {
    createDomain,
    updateDomain,
    deleteDomain,
    verifyDomain,
  };
};
